/**
 * Therapist profile editor — embedded at the top of /therapist/practice-settings.
 * Edits User.therapistInfo profile fields used on the public signup landing page.
 *
 * Photo upload talks to POST /api/therapist/profile/picture and updates
 * profilePictureUrl on save.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, TextField, Button, Avatar,
  IconButton, Chip, Switch, FormControlLabel, Alert, CircularProgress, Divider
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import api, { rawApiClient } from '../../services/api';

function ChipListEditor({ label, value, onChange, placeholder }) {
  const [draft, setDraft] = useState('');
  function add() {
    const v = draft.trim();
    if (!v) return;
    if (!value.includes(v)) onChange([...value, v]);
    setDraft('');
  }
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{label}</Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5, mb: 1 }}>
        {value.map((v, i) => (
          <Chip
            key={`${v}-${i}`}
            label={v}
            size="small"
            onDelete={() => onChange(value.filter(x => x !== v))}
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          fullWidth
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button onClick={add} size="small">Add</Button>
      </Stack>
    </Box>
  );
}

export default function TherapistProfileCard({ token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [profile, setProfile] = useState(null);
  const fileInput = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get('/therapist/profile', token);
      setProfile(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not load profile');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  function set(field, value) {
    setProfile((p) => ({ ...p, [field]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const body = {
        title: profile.title,
        displayName: profile.displayName,
        pronouns: profile.pronouns,
        qualifications: profile.qualifications,
        modalities: profile.modalities,
        languages: profile.languages,
        yearsOfExperience: profile.yearsOfExperience,
        shortBio: profile.shortBio,
        longBio: profile.longBio,
        acceptingNewClients: profile.acceptingNewClients
      };
      const res = await api.patch('/therapist/profile', body, token);
      setProfile(res.data);
      setSuccess('Profile saved');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handlePhotoSelect(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const res = await rawApiClient.post('/therapist/profile/picture', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile((p) => ({ ...p, profilePictureUrl: res.data.profilePictureUrl }));
      setSuccess('Photo uploaded');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </CardContent>
      </Card>
    );
  }

  if (!profile) return null;

  const displayName =
    profile.displayName ||
    [profile.title, profile.firstName, profile.lastName].filter(Boolean).join(' ');

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>My profile</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This is what new clients see on the signup landing page when you invite them.
          Keep the short bio under 300 characters; it&apos;s the hero text.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Avatar src={profile.profilePictureUrl || undefined} sx={{ width: 96, height: 96 }}>
              {(profile.firstName || profile.email || '?').charAt(0).toUpperCase()}
            </Avatar>
            <input
              ref={fileInput}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              hidden
              onChange={handlePhotoSelect}
            />
            <Button
              size="small"
              startIcon={uploading ? <CircularProgress size={14} /> : <PhotoCameraIcon />}
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading…' : (profile.profilePictureUrl ? 'Change photo' : 'Upload photo')}
            </Button>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField label="Title (e.g. Dr)" value={profile.title || ''} onChange={(e) => set('title', e.target.value)} sx={{ width: 140 }} size="small" />
              <TextField label="Display name" value={profile.displayName || ''} onChange={(e) => set('displayName', e.target.value)} fullWidth size="small" helperText="Overrides first+last name on public surfaces" />
              <TextField label="Pronouns" value={profile.pronouns || ''} onChange={(e) => set('pronouns', e.target.value)} sx={{ width: 160 }} size="small" />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 1.5 }}>
              <TextField
                label="Years of experience" type="number" size="small" sx={{ width: 200 }}
                value={profile.yearsOfExperience ?? ''}
                onChange={(e) => set('yearsOfExperience', e.target.value === '' ? null : Number(e.target.value))}
                inputProps={{ min: 0, max: 80 }}
              />
              <FormControlLabel
                control={<Switch checked={!!profile.acceptingNewClients} onChange={(e) => set('acceptingNewClients', e.target.checked)} />}
                label="Accepting new clients"
              />
            </Stack>
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={2}>
          <TextField
            label="Short bio (≤300 chars — shown on signup landing)" multiline minRows={2} maxRows={4}
            value={profile.shortBio || ''} onChange={(e) => set('shortBio', e.target.value.slice(0, 300))}
            helperText={`${(profile.shortBio || '').length}/300`}
          />
          <TextField
            label="Long bio (optional, ≤2000 chars)" multiline minRows={3} maxRows={10}
            value={profile.longBio || ''} onChange={(e) => set('longBio', e.target.value.slice(0, 2000))}
            helperText={`${(profile.longBio || '').length}/2000`}
          />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <ChipListEditor
              label="Qualifications"
              value={profile.qualifications || []}
              onChange={(v) => set('qualifications', v)}
              placeholder="e.g. PhD Clinical Psychology"
            />
            <ChipListEditor
              label="Modalities"
              value={profile.modalities || []}
              onChange={(v) => set('modalities', v)}
              placeholder="e.g. CBT, MBT, EMDR"
            />
          </Stack>
          <ChipListEditor
            label="Languages"
            value={profile.languages || []}
            onChange={(v) => set('languages', v)}
            placeholder="e.g. English, Español"
          />
        </Stack>

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save profile'}
          </Button>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Note: <strong>{displayName || profile.email}</strong> · {profile.email}
        </Typography>
      </CardContent>
    </Card>
  );
}
